import { useCallback, useEffect, useState } from "react";
import ReactFlow, {
  Background, Controls, MiniMap, addEdge, useNodesState, useEdgesState, Handle, Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { Plus, Trash } from "@phosphor-icons/react";

const KINDS = [
  { id: "start", label: "Start / Hälsning", color: "#10B981" },
  { id: "say", label: "Steg / Säg", color: "#0033FF" },
  { id: "branch", label: "Val / Förgrening", color: "#F59E0B" },
  { id: "tool", label: "Åtgärd / Verktyg", color: "#7C3AED" },
  { id: "escalate", label: "Eskalera", color: "#EF4444" },
  { id: "end", label: "Avsluta", color: "#71717A" },
];
const KIND_MAP = Object.fromEntries(KINDS.map((k) => [k.id, k]));

const DEFAULT_FLOW = {
  nodes: [
    { id: "n1", type: "flow", position: { x: 80, y: 20 }, data: { kind: "start", label: "Hälsning", text: "Hej, tack för att du ringer! Hur kan jag hjälpa dig?" } },
    { id: "n2", type: "flow", position: { x: 80, y: 170 }, data: { kind: "say", label: "Förstå ärendet", text: "Ta reda på vad kunden vill ha hjälp med och svara utifrån kunskapsbasen." } },
    { id: "n3", type: "flow", position: { x: 80, y: 320 }, data: { kind: "escalate", label: "Eskalera", text: "Om du inte kan lösa ärendet eller kunden ber om en människa." } },
  ],
  edges: [
    { id: "e1-2", source: "n1", target: "n2" },
    { id: "e2-3", source: "n2", target: "n3" },
  ],
};

function FlowNode({ data, selected }) {
  const k = KIND_MAP[data.kind] || KIND_MAP.say;
  return (
    <div className={`rounded-xl border-2 bg-white shadow-sm px-3 py-2 w-[210px] ${selected ? "ring-2 ring-[#0033FF] ring-offset-1" : ""}`}
      style={{ borderColor: k.color }} data-testid="callflow-node">
      <Handle type="target" position={Position.Top} style={{ background: k.color, width: 9, height: 9 }} />
      <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: k.color }}>{k.label}</div>
      <div className="text-sm font-semibold text-zinc-900 truncate">{data.label || "(namnlös)"}</div>
      {data.text ? <div className="text-xs text-zinc-500 line-clamp-2 mt-0.5">{data.text}</div> : null}
      <Handle type="source" position={Position.Bottom} style={{ background: k.color, width: 9, height: 9 }} />
    </div>
  );
}
const nodeTypes = { flow: FlowNode };

export default function CallFlowDesigner({ agent, set }) {
  const initial = (agent.call_flow && Array.isArray(agent.call_flow.nodes) && agent.call_flow.nodes.length)
    ? agent.call_flow : DEFAULT_FLOW;
  const toolOptions = (agent.workflows || []).filter((w) => w && w.id).map((w) => ({ id: w.id, name: w.name || w.id, enabled: !!w.enabled }));
  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes.map((n) => ({ ...n, type: "flow" })));
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges || []);
  const [selId, setSelId] = useState(null);

  useEffect(() => {
    set("call_flow", {
      nodes: nodes.map((n) => ({ id: n.id, type: "flow", position: n.position, data: { kind: n.data.kind, label: n.data.label || "", text: n.data.text || "", tool_id: n.data.tool_id || "", tool_name: n.data.tool_name || "" } })),
      edges: edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges]);

  const onConnect = useCallback((c) => setEdges((eds) => addEdge({ ...c, animated: true }, eds)), [setEdges]);
  const addNode = (kind) => {
    const id = `n${Date.now()}`;
    setNodes((nds) => [...nds, { id, type: "flow", position: { x: 360, y: 40 + (nds.length % 6) * 40 }, data: { kind, label: KIND_MAP[kind].label, text: "" } }]);
    setSelId(id);
  };
  const updateSel = (key, val) => setNodes((nds) => nds.map((n) => (n.id === selId ? { ...n, data: { ...n.data, [key]: val } } : n)));
  const deleteSel = () => {
    setNodes((nds) => nds.filter((n) => n.id !== selId));
    setEdges((eds) => eds.filter((e) => e.source !== selId && e.target !== selId));
    setSelId(null);
  };
  const sel = nodes.find((n) => n.id === selId);

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="callflow-designer">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg">Samtalsflöde</h3>
          <p className="text-sm text-zinc-500 mt-0.5 max-w-2xl">Rita upp hur samtalet ska gå: lägg till steg, dra pilar mellan dem och bestäm var agenten ska eskalera eller köra en åtgärd. Flödet vägleder AI:n under samtalet.</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer shrink-0">
          <span className="text-sm font-semibold text-zinc-700">Aktivera flödet</span>
          <input data-testid="callflow-enabled" type="checkbox" checked={!!agent.call_flow_enabled}
            onChange={(e) => set("call_flow_enabled", e.target.checked)} className="h-5 w-5 accent-[#0033FF]" />
        </label>
      </div>

      <div className="mt-4 flex flex-wrap gap-2" data-testid="callflow-palette">
        {KINDS.map((k) => (
          <button key={k.id} type="button" data-testid={`callflow-add-${k.id}`} onClick={() => addNode(k.id)}
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold hover:bg-zinc-50 transition-colors"
            style={{ borderColor: k.color, color: k.color }}>
            <Plus size={13} weight="bold" /> {k.label}
          </button>
        ))}
      </div>

      <div className="mt-4 grid lg:grid-cols-[1fr,300px] gap-4">
        <div className="h-[480px] rounded-xl border border-zinc-200 overflow-hidden bg-[#FAFAFA]" data-testid="callflow-canvas">
          <ReactFlow
            nodes={nodes} edges={edges} nodeTypes={nodeTypes}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            onNodeClick={(_, n) => setSelId(n.id)} onPaneClick={() => setSelId(null)}
            fitView proOptions={{ hideAttribution: true }}>
            <Background color="#d4d4d8" gap={18} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable className="!bg-white" />
          </ReactFlow>
        </div>

        <div className="rounded-xl border border-zinc-200 p-4" data-testid="callflow-editor">
          {sel ? (
            <div className="space-y-3">
              <div className="text-xs font-bold uppercase tracking-wide text-zinc-400">Redigera steg</div>
              <div>
                <label className="text-xs font-semibold text-zinc-500">Typ</label>
                <select data-testid="callflow-node-kind" value={sel.data.kind} onChange={(e) => updateSel("kind", e.target.value)}
                  className="w-full mt-1 bg-white border border-zinc-200 rounded-md px-2 py-2 text-sm">
                  {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-500">Namn</label>
                <input data-testid="callflow-node-label" value={sel.data.label || ""} onChange={(e) => updateSel("label", e.target.value)}
                  className="w-full mt-1 bg-white border border-zinc-200 rounded-md px-2 py-2 text-sm" placeholder="t.ex. Verifiera kund" />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-500">Vad ska hända här?</label>
                <textarea data-testid="callflow-node-text" rows={4} value={sel.data.text || ""} onChange={(e) => updateSel("text", e.target.value)}
                  className="w-full mt-1 bg-white border border-zinc-200 rounded-md px-2 py-2 text-sm" placeholder="Beskriv vad agenten ska säga eller göra i det här steget…" />
              </div>
              {sel.data.kind === "tool" && (
                <div data-testid="callflow-tool-picker">
                  <label className="text-xs font-semibold text-zinc-500">Koppla till workflow</label>
                  <select data-testid="callflow-node-tool" value={sel.data.tool_id || ""}
                    onChange={(e) => {
                      const opt = toolOptions.find((o) => o.id === e.target.value);
                      setNodes((nds) => nds.map((n) => (n.id === selId ? { ...n, data: { ...n.data, tool_id: e.target.value, tool_name: opt?.name || "" } } : n)));
                    }}
                    className="w-full mt-1 bg-white border border-zinc-200 rounded-md px-2 py-2 text-sm">
                    <option value="">— Inget kopplat (bara beskrivning) —</option>
                    {toolOptions.map((o) => <option key={o.id} value={o.id}>{o.name}{o.enabled ? "" : " (inaktiverat)"}</option>)}
                  </select>
                  {toolOptions.length === 0
                    ? <p className="text-xs text-amber-600 mt-1">Inga workflows definierade. Skapa ett under fliken <strong>Workflows</strong> först.</p>
                    : <p className="text-xs text-zinc-400 mt-1">Agenten anropar detta workflow automatiskt när steget nås under samtalet.</p>}
                </div>
              )}
              <button data-testid="callflow-delete-node" onClick={deleteSel}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500 hover:text-red-600">
                <Trash size={15} weight="bold" /> Ta bort steg
              </button>
            </div>
          ) : (
            <p className="text-sm text-zinc-400">Klicka på ett steg i flödet för att redigera det, eller lägg till ett nytt steg ovan. Dra från en nods undersida till en annans översida för att koppla ihop dem.</p>
          )}
        </div>
      </div>
      {!agent.call_flow_enabled && (
        <p className="mt-3 text-xs text-amber-600" data-testid="callflow-inactive-note">Flödet sparas men styr inte agenten förrän du aktiverar det ovan.</p>
      )}
    </div>
  );
}
